import { useEffect, useRef, useState, type Ref } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import { checkOpenSubtitles, getPrefs, patchPrefs } from "../../../lib/libraryAdmin";
import type { ServerConnection } from "../../../lib/serverConnection";
import {
  accountLine,
  failureWords,
  formLine,
  isRefusal,
  looksLikeEmail,
  openSubtitlesStatus,
  openSubtitlesView,
  type OpenSubtitlesFailure,
  type OpenSubtitlesFormLine,
  type OpenSubtitlesPhase,
  type OpenSubtitlesStatus,
} from "../../../lib/openSubtitlesStatus";
import { AlertCircleIcon, CaptionsIcon, RefreshIcon } from "../../../components/icons";
import { PasswordInput } from "../../../components/password-input";
import { Chip, ErrorNote, Spinner } from "../../../components/ui";
import {
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetNotice,
  SheetSection,
  StateChip,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/opensubtitles")({
  component: ServerOpenSubtitlesSettings,
});

/** The line under the two fields: both describe themselves by it. */
const LINE_ID = "os-line";

const NO_ACCOUNT: OpenSubtitlesStatus = { kind: "none" };

type StoredAccount = Extract<OpenSubtitlesStatus, { kind: "ok" | "unverified" }>;

/** "No account": clearing the username clears both. */
function forget(conn: ServerConnection) {
  return patchPrefs(conn, { openSubtitlesUsername: "", openSubtitlesPassword: "" });
}

/**
 * The Emby-model Open Subtitles page, in Kenny's Claude Design (2026-10):
 * the admin connects ONE opensubtitles.com account that raises the server's
 * download quota. With none it is a sign-in form over the two plans' daily
 * limits; with one it is that account's card. The application Api-Key is
 * server config and is not shown — the server only reports whether it has
 * one, which gates the logins.
 *
 * Nothing here is a draft, so there is no Save: Sign in stores the account
 * and asks Open Subtitles at once. The page reads the server-persisted
 * verdict of the last real login (prefs.openSubtitlesAccount) and never logs
 * in on view; Check now is the one fresh login.
 *
 * An account Open Subtitles refuses is not left stored. The server refuses
 * subtitle downloads with a rejected account instead of going anonymous, so
 * a sign-in that was refused (or could not be checked) puts back "no
 * account", and a Check now that is refused removes the account and opens
 * the form on its username. The one refusal the page cannot catch is a lazy
 * login from the search dialog: the form then says so, with a way to remove
 * the account.
 */
function ServerOpenSubtitlesSettings() {
  const { serverId } = Route.useParams();
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // prefs query would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const qc = useQueryClient();

  const prefs = useQuery({
    queryKey: ["settings", "prefs", serverId],
    enabled: isServer,
    queryFn: () => getPrefs((source as ServerSource).conn),
  });

  const supported = Boolean(prefs.data && "openSubtitlesUsername" in prefs.data);
  const apiKeySet = prefs.data?.openSubtitlesApiKeySet === true;
  const status = prefs.data ? openSubtitlesStatus(prefs.data) : NO_ACCOUNT;

  // A flow holds the status it started from: the server's events refetch
  // prefs while it runs, and the page must not change under the button.
  const [flow, setFlow] = useState<{
    phase: Exclude<OpenSubtitlesPhase, "idle">;
    from: OpenSubtitlesStatus;
  } | null>(null);
  const [failure, setFailure] = useState<OpenSubtitlesFailure | null>(null);
  // null = not typed in: the field shows the name of a stored account that
  // was refused. The password never round-trips.
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  // An address was in Username when the field was left.
  const [emailSeen, setEmailSeen] = useState(false);

  const phase = flow?.phase ?? "idle";
  const shown = flow?.from ?? status;
  const view = openSubtitlesView(shown, phase, failure);
  const name = username ?? (shown.kind === "rejected" ? shown.username : "");

  // Where the focus goes when a flow ends: its fields were disabled, or the
  // form and the card changed places under it.
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const checkRef = useRef<HTMLButtonElement>(null);
  const signOutRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<"username" | "password" | "account" | null>(null);
  useEffect(() => {
    if (flow) return;
    const next = focusNext.current;
    focusNext.current = null;
    if (next === "username") usernameRef.current?.focus();
    else if (next === "password") passwordRef.current?.focus();
    else if (next === "account") (checkRef.current ?? signOutRef.current)?.focus();
  }, [flow]);

  function begin(next: Exclude<OpenSubtitlesPhase, "idle">) {
    setFlow({ phase: next, from: status });
    setFailure(null);
  }
  async function settle() {
    await qc.invalidateQueries({ queryKey: ["settings", "prefs", serverId] });
    setFlow(null);
  }
  function emptyForm() {
    setUsername(null);
    setPassword("");
    setEmailSeen(false);
  }

  async function signIn() {
    if (!isServer || flow) return;
    const user = name.trim();
    if (user === "" || password === "") {
      (user === "" ? usernameRef : passwordRef).current?.focus();
      return;
    }
    const active = document.activeElement;
    const from =
      active === usernameRef.current ? "username" : active === passwordRef.current ? "password" : null;
    begin("signing-in");
    let stored = false;
    try {
      await patchPrefs(source.conn, { openSubtitlesUsername: user, openSubtitlesPassword: password });
      stored = true;
      // Without the application key the server cannot ask Open Subtitles:
      // the account is stored unchecked.
      if (apiKeySet) await checkOpenSubtitles(source.conn);
      emptyForm();
      focusNext.current = "account";
    } catch (e) {
      // Refused, or not checked: not kept (see the component's note).
      if (stored) await forget(source.conn).catch(() => {});
      if (isRefusal(e)) {
        setFailure({ kind: "refused", email: looksLikeEmail(user) });
        focusNext.current = "username";
      } else {
        setFailure({ kind: "other", message: failureWords(e) });
        focusNext.current = from;
      }
    } finally {
      await settle();
    }
  }

  // A fresh real login with the stored account; its verdict lands in
  // prefs, which the card reads.
  async function checkNow() {
    if (!isServer || flow) return;
    if (status.kind !== "ok" && status.kind !== "unverified") return;
    const stored = status.username;
    begin("checking");
    try {
      await checkOpenSubtitles(source.conn);
    } catch (e) {
      if (isRefusal(e)) {
        // The stored password no longer works: the account goes, and the
        // form opens on its name.
        await forget(source.conn).catch(() => {});
        setUsername(stored);
        setPassword("");
        setEmailSeen(false);
        setFailure({ kind: "expired" });
        focusNext.current = "password";
      } else {
        setFailure({ kind: "other", message: failureWords(e) });
      }
    } finally {
      await settle();
    }
  }

  // The card's Sign out, and the form's "Remove the account" for one the
  // server still holds.
  async function signOut() {
    if (!isServer || flow) return;
    begin("signing-out");
    try {
      await forget(source.conn);
      emptyForm();
      focusNext.current = "username";
    } catch (e) {
      setFailure({ kind: "other", message: failureWords(e) });
    } finally {
      await settle();
    }
  }

  return (
    <ServerSettingsGuard serverId={serverId} heading="Open Subtitles">
      {(server) => (
        <div>
          <SheetHeader
            title="Open Subtitles"
            lede={supported ? view.lede : undefined}
            status={supported ? <StateChip {...view.pill} live /> : null}
          />

          {prefs.isLoading || !prefs.data ? (
            // A load that failed says so below instead of spinning for good.
            prefs.error ? null : (
              <SheetLoading />
            )
          ) : !supported ? (
            <SheetNotice title="Open Subtitles">
              This server doesn't support subtitle search yet — update rago-server.
            </SheetNotice>
          ) : view.body === "account" && (shown.kind === "ok" || shown.kind === "unverified") ? (
            <SheetSection>
              <AccountCard
                account={shown}
                phase={phase}
                failure={failure}
                canCheck={apiKeySet && server.owned}
                checkRef={checkRef}
                signOutRef={signOutRef}
                onCheck={() => void checkNow()}
                onSignOut={() => void signOut()}
              />
            </SheetSection>
          ) : (
            <SheetSection>
              <form
                noValidate
                className="pt-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void signIn();
                }}
              >
                <h2 className="rago-caps">Account</h2>
                <SignInFields
                  username={name}
                  password={password}
                  line={formLine(shown, failure, emailSeen)}
                  phase={phase}
                  usernameRef={usernameRef}
                  passwordRef={passwordRef}
                  onUsername={(v) => {
                    setUsername(v);
                    setFailure(null);
                    // Typing only ever takes the line away.
                    if (!looksLikeEmail(v)) setEmailSeen(false);
                  }}
                  onUsernameBlur={() => {
                    if (looksLikeEmail(name)) setEmailSeen(true);
                  }}
                  onPassword={(v) => {
                    setPassword(v);
                    setFailure(null);
                  }}
                  onRemove={() => void signOut()}
                />
              </form>

              <h2 className="rago-caps mt-10">Daily download limit</h2>
              <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-3.5">
                <LimitTile plan="Free account" perDay={20} />
                <LimitTile plan="VIP" perDay={1000} />
              </div>
            </SheetSection>
          )}

          {prefs.error ? (
            <SheetInset>
              <ErrorNote error={prefs.error} />
            </SheetInset>
          ) : null}
        </div>
      )}
    </ServerSettingsGuard>
  );
}

const LABEL = "mb-2.5 block text-sm font-semibold text-[var(--rago-text-1)]";

/** The two fields, the one line under both, and Sign in. */
function SignInFields(props: {
  username: string;
  password: string;
  line: OpenSubtitlesFormLine;
  phase: OpenSubtitlesPhase;
  usernameRef: Ref<HTMLInputElement>;
  passwordRef: Ref<HTMLInputElement>;
  onUsername: (value: string) => void;
  onUsernameBlur: () => void;
  onPassword: (value: string) => void;
  onRemove: () => void;
}) {
  const { line, phase } = props;
  const signingIn = phase === "signing-in";
  const ready = props.username.trim() !== "" && props.password !== "";
  return (
    <>
      <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-4">
        <div>
          <label htmlFor="os-username" className={LABEL}>
            Username
          </label>
          {/* Not autocomplete="username": the browser would put the Rago
              sign-in into this form. */}
          <input
            ref={props.usernameRef}
            id="os-username"
            className="rago-input rago-input-lg"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="opensubtitles.com username"
            value={props.username}
            disabled={signingIn}
            aria-invalid={line.invalid === "username" || undefined}
            aria-describedby={LINE_ID}
            onChange={(e) => props.onUsername(e.target.value)}
            onBlur={props.onUsernameBlur}
          />
        </div>
        <div>
          <label htmlFor="os-password" className={LABEL}>
            Password
          </label>
          <PasswordInput
            ref={props.passwordRef}
            id="os-password"
            size="lg"
            autoComplete="new-password"
            value={props.password}
            disabled={signingIn}
            // The stored password is the wrong one, not the one being typed.
            invalid={line.invalid === "password" && props.password === ""}
            describedBy={LINE_ID}
            onChange={props.onPassword}
          />
        </div>
      </div>

      {line.tone === "hint" ? (
        <p id={LINE_ID} className="mt-3 text-sm text-[var(--rago-text-3)]">
          {line.text}
        </p>
      ) : (
        <div className="mt-3 flex items-start gap-2 text-sm font-medium text-[var(--rago-error)]">
          <AlertCircleIcon size={16} className="mt-0.5 shrink-0" />
          <p>
            <span id={LINE_ID} role="alert">
              {line.text}
            </span>
            {line.stored ? (
              <>
                {" "}
                <button
                  type="button"
                  className="rounded-sm text-[var(--rago-text-1)] underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-[var(--rago-ring)] focus-visible:outline-none aria-disabled:opacity-55"
                  aria-disabled={phase !== "idle" ? true : undefined}
                  onClick={() => {
                    if (phase === "idle") props.onRemove();
                  }}
                >
                  Remove the account
                </button>
              </>
            ) : null}
          </p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
        {/* Resting until both fields hold something; a press then goes to
            the empty one. */}
        <button
          type="submit"
          className={ready ? "rago-btn" : "rago-btn rago-btn-quiet"}
          aria-disabled={!ready || phase !== "idle" ? true : undefined}
        >
          {signingIn ? <Spinner size={15} /> : null}
          {signingIn ? "Signing in" : "Sign in"}
        </button>
        <p className="text-sm text-[var(--rago-text-3)]">
          No account?{" "}
          <a
            href="https://www.opensubtitles.com"
            target="_blank"
            rel="noreferrer"
            className="text-[var(--rago-text-1)] underline underline-offset-4"
          >
            Create one on opensubtitles.com
          </a>
        </p>
      </div>
    </>
  );
}

/** What a plan allows in a day, before an account is connected. */
function LimitTile(props: { plan: string; perDay: number }) {
  return (
    <div className="rounded-[var(--rago-radius-l)] border border-[var(--rago-line-1)] bg-white/[0.03] px-5 pt-5 pb-6">
      <p className="text-sm font-semibold text-[var(--rago-text-2)]">{props.plan}</p>
      <p className="mt-4 flex items-baseline gap-2">
        <span className="rago-display text-[34px] leading-none text-[var(--rago-text-1)] tabular-nums">
          {props.perDay}
        </span>
        <span className="text-sm text-[var(--rago-text-3)]">subtitles a day</span>
      </p>
    </div>
  );
}

/**
 * The one account: its name and plan, when Open Subtitles last confirmed
 * it, Check now and Sign out. The plan chip is the server's verdict
 * rendered by planLabel.
 */
function AccountCard(props: {
  account: StoredAccount;
  phase: OpenSubtitlesPhase;
  failure: OpenSubtitlesFailure | null;
  /** The server holds the application key, so it can ask Open Subtitles. */
  canCheck: boolean;
  checkRef: Ref<HTMLButtonElement>;
  signOutRef: Ref<HTMLButtonElement>;
  onCheck: () => void;
  onSignOut: () => void;
}) {
  const { account, phase } = props;
  const resting = phase !== "idle";
  const line = accountLine(account, phase, props.failure);
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-4 rounded-[var(--rago-radius-l)] border border-[var(--rago-line-1)] bg-white/[0.03] py-4 pr-4 pl-5">
      <div className="flex min-w-0 items-center gap-4">
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-white/[0.07] text-[var(--rago-text-1)]"
        >
          <CaptionsIcon size={22} />
        </span>
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <span className="text-[17px] font-semibold [overflow-wrap:anywhere] text-[var(--rago-text-1)]">
              {account.username}
            </span>
            {account.kind === "ok" ? (
              <Chip tone="ok" size="md">
                {account.label}
              </Chip>
            ) : null}
          </p>
          <p
            role="status"
            className={`mt-1 text-sm ${
              line.tone === "error" ? "text-[var(--rago-error)]" : "text-[var(--rago-text-3)]"
            }`}
          >
            {line.text}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        {props.canCheck ? (
          <button
            ref={props.checkRef}
            type="button"
            className="rago-btn rago-btn-quiet rago-btn-sm"
            aria-disabled={resting ? true : undefined}
            onClick={() => {
              if (!resting) props.onCheck();
            }}
          >
            {phase === "checking" ? <Spinner size={12} /> : <RefreshIcon size={14} />}
            {phase === "checking" ? "Checking" : "Check now"}
          </button>
        ) : null}
        <button
          ref={props.signOutRef}
          type="button"
          className="rago-btn rago-btn-danger rago-btn-sm"
          aria-disabled={resting ? true : undefined}
          onClick={() => {
            if (!resting) props.onSignOut();
          }}
        >
          {phase === "signing-out" ? <Spinner size={12} /> : null}
          Sign out
        </button>
      </div>
    </div>
  );
}
